import React, { useEffect, useState } from "react";
import { Loader2, Plus, Power, RefreshCw, Search, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../api";
import { fmtDate, useLang } from "../i18n";
import { PlatformUserActions } from "./PlatformUserActions";

const ROLES = ["admin", "operator", "auditor", "client", "provider"];
const EMPTY = { name: "", email: "", password: "", role: "operator", tenant_id: "", send_credentials: true };
const EMPTY_SA = { name: "", email: "", password: "", confirm: "" };

export const PlatformUsers = ({ tenants }) => {
  const { t } = useLang();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [create, setCreate] = useState(null);
  const [sa, setSa] = useState(null);
  const [busy, setBusy] = useState(false);
  const orgs = (tenants || []).filter((x) => x.status !== "trashed");

  const load = () => api.get("/platform/all-users").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const submitSa = async (e) => {
    e.preventDefault();
    if (sa.password !== sa.confirm) return toast.error(t("pw.mismatch"));
    setBusy(true);
    try {
      await api.post("/platform/superadmins", sa);
      toast.success(t("pu.sa_created"));
      setSa(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/platform/users", create);
      toast.success(t("pu.created"));
      setCreate(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/platform/users/${u.id}`, { active: u.active === false });
      toast.success(u.active === false ? t("pu.activated") : t("pu.deactivated"));
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const term = q.trim().toLowerCase();
  const list = (rows || []).filter((u) => !term || [u.name, u.email, u.tenant_name, u.role].some((v) => (v || "").toLowerCase().includes(term)));

  return (
    <div className="ec-card p-6" data-testid="platform-users-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-heading font-bold text-slate-900 flex items-center gap-2"><Users className="w-5 h-5 text-[#044CBC]" /> {t("pu.title")} {rows && <span className="text-xs font-semibold text-slate-500" data-testid="pu-count">({list.length})</span>}</h3>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input className="ec-input !pl-8 !py-1.5 !text-xs w-56" placeholder={t("pu.search_ph")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="pu-search-input" />
          </div>
          <button onClick={load} className="p-2 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50" title={t("common.refresh")} data-testid="pu-refresh"><RefreshCw className="w-4 h-4" /></button>
          <button onClick={() => setSa({ ...EMPTY_SA })} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16335F] text-white text-xs font-bold hover:bg-[#0A192F]" data-testid="pu-create-sa-button">
            <ShieldCheck className="w-4 h-4" /> {t("pu.create_sa")}
          </button>
          <button onClick={() => setCreate({ ...EMPTY, tenant_id: orgs[0]?.id || "" })} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#044CBC] text-white text-xs font-bold hover:bg-[#033A8C]" data-testid="pu-create-button">
            <Plus className="w-4 h-4" /> {t("pu.create")}
          </button>
        </div>
      </div>

      {rows === null ? (
        <div className="p-8 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-slate-200" data-testid="pu-table">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-[#16335F] text-white text-left text-xs font-bold uppercase tracking-wide">
                <th className="px-3 py-2">{t("u.name")}</th><th className="px-3 py-2">{t("u.email")}</th><th className="px-3 py-2">{t("ah.tenant")}</th>
                <th className="px-3 py-2">{t("u.role")}</th><th className="px-3 py-2">{t("u.status")}</th><th className="px-3 py-2">{t("pf.last_login")}</th>
                <th className="px-3 py-2 text-right">{t("pu.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="pu-empty">—</td></tr>
              ) : list.map((u, i) => (
                <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`pu-row-${i}`}>
                  <td className="px-3 py-2 text-xs font-semibold text-slate-800">{u.name}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{u.email}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{u.tenant_name || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-700">{u.role === "superadmin" ? <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#16335F] text-white" data-testid={`pu-sa-badge-${i}`}><ShieldCheck className="w-3 h-3" /> {t("u.superadmin")}</span> : t(`u.${u.role}`)}</td>
                  <td className="px-3 py-2">
                    <button type="button" onClick={() => toggle(u)} data-testid={`pu-toggle-${i}`} title={u.active === false ? t("common.activate") : t("u.deactivate")}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full border transition-colors ${u.active === false ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100" : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100"}`}>
                      <Power className="w-3 h-3" /> {u.active === false ? t("u.inactive") : t("u.active")}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-600">{fmtDate(u.last_login_at)}</td>
                  <td className="px-3 py-2"><PlatformUserActions user={u} index={i} tenants={orgs} onChanged={load} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sa && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="pu-sa-modal" onClick={() => setSa(null)}>
          <form onSubmit={submitSa} className="ec-card p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#16335F]" /> {t("pu.create_sa")}</h3>
            <p className="text-xs text-slate-500">{t("pu.sa_help")}</p>
            <div><label className="ec-label">{t("u.name")}</label><input className="ec-input" value={sa.name} onChange={(e) => setSa({ ...sa, name: e.target.value })} required minLength={2} data-testid="pu-sa-name" /></div>
            <div><label className="ec-label">{t("u.email")}</label><input className="ec-input" type="email" value={sa.email} onChange={(e) => setSa({ ...sa, email: e.target.value })} required data-testid="pu-sa-email" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="ec-label">{t("pu.temp_password")}</label><input className="ec-input" type="password" value={sa.password} onChange={(e) => setSa({ ...sa, password: e.target.value })} required minLength={8} autoComplete="new-password" data-testid="pu-sa-password" /></div>
              <div><label className="ec-label">{t("pw.confirm")}</label><input className="ec-input" type="password" value={sa.confirm} onChange={(e) => setSa({ ...sa, confirm: e.target.value })} required minLength={8} autoComplete="new-password" data-testid="pu-sa-confirm" /></div>
            </div>
            {sa.confirm && sa.password !== sa.confirm && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="pu-sa-mismatch">{t("pw.mismatch")}</div>}
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={busy || sa.password !== sa.confirm} data-testid="pu-sa-save" className="flex-1 px-4 py-2.5 rounded-lg bg-[#16335F] text-white text-sm font-bold hover:bg-[#0A192F] disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("pu.create_sa")}</button>
              <button type="button" onClick={() => setSa(null)} data-testid="pu-sa-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">{t("u.cancel")}</button>
            </div>
          </form>
        </div>
      )}

      {create && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4" data-testid="pu-create-modal" onClick={() => setCreate(null)}>
          <form onSubmit={submit} className="ec-card p-6 w-full max-w-md space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-lg font-extrabold text-slate-900 flex items-center gap-2"><Plus className="w-5 h-5 text-[#044CBC]" /> {t("pu.create")}</h3>
            <div><label className="ec-label">{t("u.name")}</label><input className="ec-input" value={create.name} onChange={(e) => setCreate({ ...create, name: e.target.value })} required minLength={2} data-testid="pu-create-name" /></div>
            <div><label className="ec-label">{t("u.email")}</label><input className="ec-input" type="email" value={create.email} onChange={(e) => setCreate({ ...create, email: e.target.value })} required data-testid="pu-create-email" /></div>
            <div><label className="ec-label">{t("pu.temp_password")}</label><input className="ec-input" type="text" value={create.password} onChange={(e) => setCreate({ ...create, password: e.target.value })} required minLength={8} autoComplete="off" data-testid="pu-create-password" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="ec-label">{t("u.role")}</label>
                <select className="ec-input" value={create.role} onChange={(e) => setCreate({ ...create, role: e.target.value })} data-testid="pu-create-role">{ROLES.map((r) => <option key={r} value={r}>{t(`u.${r}`)}</option>)}</select></div>
              <div><label className="ec-label">{t("ah.tenant")}</label>
                <select className="ec-input" value={create.tenant_id} onChange={(e) => setCreate({ ...create, tenant_id: e.target.value })} required data-testid="pu-create-tenant">
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select></div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid="pu-create-send-label">
              <input type="checkbox" className="accent-[#044CBC] w-4 h-4" checked={create.send_credentials} onChange={(e) => setCreate({ ...create, send_credentials: e.target.checked })} data-testid="pu-create-send" /> {t("pu.send_credentials")}
            </label>
            <div className="flex gap-3 pt-1">
              <button type="submit" disabled={busy} data-testid="pu-create-save" className="flex-1 px-4 py-2.5 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] disabled:opacity-60 inline-flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />}{t("pu.create")}</button>
              <button type="button" onClick={() => setCreate(null)} data-testid="pu-create-cancel" className="px-4 py-2.5 rounded-lg border border-slate-200 text-sm font-bold text-slate-600 hover:bg-slate-50">{t("u.cancel")}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};
